import React, { lazy } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Switch, useHistory, useLocation } from 'react-router-dom';

import ConfirmCancel from 'components/common/Dialogs/ConfirmCancel';
import LWDialog from 'components/common/LWDialog';
import LWTypography from 'components/common/LWTypography';
import SupportLayout from 'components/standard/SupportLayout';
import { username as usernameSelector } from 'modules/auth/authSelectors';
import useSupportRequestAuthenticate from 'modules/queries/support/request/useAuthenticate';
import { roles } from 'utility/constants/roles';
import RoleRestrictedRoute from 'utility/routing/RoleRestrictedRoute';

const MyCases = lazy(() => import('containers/pages/support/MyCases'));
const FeaturedHelpDocs = lazy(() => import('containers/pages/support'));
const LiquidWebStatusReports = lazy(
	() => import('containers/pages/support/LiquidWebStatusReports'),
);

const AuthenticateSupportRequest = () => {
	const location = useLocation();
	const history = useHistory();
	const loggedInUser = useSelector(usernameSelector);

	const searchParams = new URLSearchParams(location.search);
	const caseId = searchParams.get('case_id') || searchParams.get('id');

	const { mutate: authenticate, isLoading } = useSupportRequestAuthenticate();

	const close = () => history.push('/');

	const handleConfirm = () => {
		if (!caseId) {
			// no-op
			return;
		}

		authenticate(
			{ id: caseId, username: loggedInUser },
			{
				onSuccess: () => {
					close();
				},
			},
		);
	};

	return (
		<LWDialog open title="Confirmation Needed" onClose={close}>
			<LWTypography>
				You are signed in as {loggedInUser}. Please confirm you would like to
				authenticate this support request with this account.
			</LWTypography>
			<ConfirmCancel
				isLoading={isLoading}
				cancel={close}
				confirm={handleConfirm}
			/>
		</LWDialog>
	);
};

const SupportRouter = () => {
	const { pathname } = useLocation();
	return (
		<SupportLayout tabsValue={pathname}>
			<Switch>
				<RoleRestrictedRoute
					// legacy route
					exact
					whitelistedRoles={[
						roles.PURCHASER,
						roles.TECHNICIAN,
						roles.SUSPENDED_OWNER,
					]}
					path="/support/mytickets"
					component={() => <Redirect to="/support/mycases" />}
				/>
				<RoleRestrictedRoute
					exact
					whitelistedRoles={[
						roles.PURCHASER,
						roles.TECHNICIAN,
						roles.SUSPENDED_OWNER,
					]}
					path="/support/mycases"
					component={MyCases}
				/>
				<RoleRestrictedRoute
					exact
					whitelistedRoles={[roles.PURCHASER, roles.TECHNICIAN]}
					path="/support/request/authenticate"
					component={AuthenticateSupportRequest}
				/>
				<RoleRestrictedRoute
					exact
					whitelistedRoles={[
						roles.PURCHASER,
						roles.TECHNICIAN,
						roles.SUSPENDED_OWNER,
					]}
					path="/support/status"
					component={LiquidWebStatusReports}
				/>
				<RoleRestrictedRoute
					exact
					whitelistedRoles={[
						roles.PURCHASER,
						roles.TECHNICIAN,
						roles.SUSPENDED_OWNER,
					]}
					path="/support"
					component={FeaturedHelpDocs}
				/>
			</Switch>
		</SupportLayout>
	);
};

export default SupportRouter;
